import React, { useState, useRef, useLayoutEffect } from "react";
import { Loader2, Info, AlertTriangle, ArrowUp, ArrowDown, Copy, Check } from "lucide-react";
import { formatDate } from "../../utils/formatDate"; 
import ColumnSettings from "./ColumnSettings";

export default function ResultsTable({ 
  columns, 
  visibleColumns, 
  results, 
  isLoading, 
  isDataFetched, 
  error, 
  onSort, 
  sortConfig,
  searchTerm,
  setSearchTerm,
  dateRange,
  setDateRange,
  filterAfterDate,
  setFilterAfterDate,
  columnFilters,
  setColumnFilters,
  onToggleColumn,
  isSettingsOpen,
  onToggleSettings,
}) {
  const [isCopied, setIsCopied] = useState(false);
  const [draftDate, setDraftDate] = useState(filterAfterDate);

  // Sync draftDate with prop if prop changes (e.g. cleared from parent or workspace change)
  React.useEffect(() => {
    setDraftDate(filterAfterDate);
  }, [filterAfterDate]);

  const handleApplyDate = () => {
    setFilterAfterDate(draftDate);
  };

  const handleCopyIPs = () => {
    const ips = [...new Set(results.map(r => r.senderIp).filter(ip => ip && ip !== '—' && ip !== 'N/A'))];
    if (ips.length > 0) {
      navigator.clipboard.writeText(ips.join('\n'));
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }
  };

  const handleColumnFilterChange = (key, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };
  
  // --- RESIZE LOGIC ---
  const [columnWidths, setColumnWidths] = useState({});
  const resizingRef = useRef(null);

  const handleMouseDown = (e, key) => {
    e.preventDefault();
    const thElement = e.target.closest('th');
    const currentDOMWidth = thElement.getBoundingClientRect().width;
    const startX = e.clientX;
    
    resizingRef.current = { key, startX, startWidth: currentDOMWidth };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (!resizingRef.current) return;
    const { key, startX, startWidth } = resizingRef.current;
    const deltaX = e.clientX - startX;
    const newWidth = Math.max(40, startWidth + deltaX);
    setColumnWidths((prev) => ({ ...prev, [key]: newWidth }));
  };

  const handleMouseUp = () => {
    resizingRef.current = null;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  // --- DOUBLE SCROLL LOGIC ---
  const topScrollRef = useRef(null);
  const tableScrollRef = useRef(null);
  const [contentWidth, setContentWidth] = useState(0);

  useLayoutEffect(() => {
    if (tableScrollRef.current) {
      setContentWidth(tableScrollRef.current.scrollWidth);
    }
  }, [results, visibleColumns, columnWidths]);

  const handleTopScroll = (e) => {
    if (tableScrollRef.current) {
      tableScrollRef.current.scrollLeft = e.target.scrollLeft;
    }
  };

  const handleTableScroll = (e) => {
    if (topScrollRef.current) {
      topScrollRef.current.scrollLeft = e.target.scrollLeft;
    }
  };

  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'ascending' ? <ArrowUp size={16} className="ms-2" /> : <ArrowDown size={16} className="ms-2" />;
    }
    return null;
  };

  return (
    <div className="dashboard-card">
      {/* Header */}
      <div className="card-header bg-secondary text-white d-flex flex-wrap justify-content-between align-items-center py-2 px-3 gap-2">
        <h5 className="mb-0">Results</h5>
        <div className="d-flex align-items-center gap-2 flex-grow-1 ms-md-3">
          <select 
            className="form-select form-select-sm" 
            style={{ width: 'auto', minWidth: '120px' }}
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
          >
            <option value="all">All Dates</option>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="last3days">Last 3 Days</option>
            <option value="last7days">Last 7 Days</option>
          </select>

          <div className="d-flex align-items-center bg-dark rounded px-2 gap-1 border border-secondary">
            <span className="small text-white-50 text-nowrap" style={{ fontSize: '11px' }}>After:</span>
            <input
              type="datetime-local"
              className="form-control form-control-sm border-0 bg-transparent text-white shadow-none"
              style={{ fontSize: '11px', colorScheme: 'dark', width: '160px' }}
              value={draftDate}
              onChange={(e) => setDraftDate(e.target.value)}
            />
            <button 
              className="btn btn-xs btn-primary py-0 px-2" 
              style={{ fontSize: '10px', height: '20px' }}
              onClick={handleApplyDate}
            >
              Apply
            </button>
            {filterAfterDate && (
              <button 
                className="btn btn-sm btn-link text-white-50 p-0 text-decoration-none ms-1" 
                onClick={() => {
                   setDraftDate('');
                   setFilterAfterDate('');
                }}
                title="Clear date filter"
              >
                ×
              </button>
            )}
          </div>

          <input
            type="text"
            className="form-control form-control-sm flex-grow-1"
            placeholder="Global search in results..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          
          <button 
            type="button"
            className={`btn btn-sm ${isCopied ? 'btn-success' : 'btn-dark'} d-flex align-items-center gap-1 border-light`}
            onClick={handleCopyIPs}
            title="Copy all visible IPs"
            disabled={results.length === 0}
          >
            {isCopied ? <Check size={14} /> : <Copy size={14} />}
            <span className="d-none d-md-inline">Copy IPs</span>
          </button>
          
          <div className="flex-shrink-0">
            <ColumnSettings
              columns={columns}
              onToggle={onToggleColumn}
              isOpen={isSettingsOpen}
              onToggleOpen={onToggleSettings}
            />
          </div>
        </div>
      </div>

      <div className="card-body p-0">
        
        {/* Top Scrollbar */}
        <div 
          className="top-scroll-wrapper" 
          ref={topScrollRef} 
          onScroll={handleTopScroll}
        >
          <div 
            className="top-scroll-content" 
            style={{ width: `${contentWidth}px` }} 
          ></div>
        </div>

        {/* Table Wrapper */}
        <div 
          className="table-wrapper" 
          ref={tableScrollRef} 
          onScroll={handleTableScroll}
        >
          <table className="table table-hover table-bordered mb-0 results-table" style={{ tableLayout: 'auto', width: 'max-content', minWidth: '100%' }}>
            <thead className="table-dark">
              <tr>
                {visibleColumns.map((col) => (
                  <th 
                    key={col.key} 
                    scope="col" 
                    style={{ 
                      width: columnWidths[col.key] 
                        ? `${columnWidths[col.key]}px` 
                        : (col.key === 'dkim' ? '220px' : 'auto'),
                      minWidth: '40px', 
                      cursor: 'pointer',
                      position: 'relative',
                      whiteSpace: 'nowrap' 
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between" onClick={() => onSort(col.key)}>
                      <span className="text-truncate me-2">{col.name}</span>
                      {getSortIcon(col.key)}
                    </div>
                    <div 
                      className="resizer"
                      onMouseDown={(e) => handleMouseDown(e, col.key)}
                      onClick={(e) => e.stopPropagation()} 
                    />
                  </th>
                ))}
              </tr>
              {/* Filter Row */}
              <tr className="bg-light">
                {visibleColumns.map((col) => (
                  <th key={`filter-${col.key}`} className="p-1 bg-light border-secondary">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder={`Filter ${col.name}...`}
                      style={{ fontSize: '11px', padding: '2px 5px' }}
                      value={columnFilters[col.key] || ''}
                      onChange={(e) => handleColumnFilterChange(col.key, e.target.value)}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={visibleColumns.length} className="text-center py-4 text-primary">
                    <Loader2 size={24} className="spin-animation d-inline-block me-2" /> Fetching results...
                  </td>
                </tr>
              )}
              
              {error && (
                <tr>
                  <td colSpan={visibleColumns.length} className="text-center py-4">
                    <div className="alert alert-danger d-inline-block border-0 m-0">
                      <AlertTriangle size={20} className="me-2" /> {error}
                    </div>
                  </td>
                </tr>
              )}

              {results.length > 0 && results.map((row, idx) => (
                <tr key={row.id || idx}>
                  {visibleColumns.map((col) => {
                    const filterableKeys = ['senderIp', 'subject', 'from', 'domain'];
                    const isFilterable = filterableKeys.includes(col.key);
                    const rawValue = col.key === 'domain' 
                      ? (row.domain || row.auth?.domain)
                      : row[col.key];
                    const cellValue = rawValue || '—';

                    return (
                      <td 
                        key={col.key} 
                        className={`col-${col.key} ${isFilterable && cellValue !== '—' ? 'clickable-filter' : ''}`} 
                        onClick={() => {
                          if (isFilterable && cellValue !== '—') {
                            const valStr = String(cellValue);
                            setSearchTerm(prev => prev === valStr ? '' : valStr);
                          }
                        }}
                        style={{ 
                          wordBreak: 'break-word', 
                          whiteSpace: 'normal',
                          verticalAlign: 'top',
                          minWidth: '40px',
                        }}
                      >
                        {col.key === 'spfStatus' || col.key === 'dkimStatus' || col.key === 'dmarcStatus' ? (
                          (() => {
                             const rawVal = row[col.key] ? String(row[col.key]) : 'UNKNOWN';
                             const valLower = rawVal.toLowerCase();
                             
                             let badgeClass = 'badge-soft-danger';
                             let displayText = rawVal; 

                             if (valLower.includes('pass')) {
                                badgeClass = 'badge-soft-success';
                              } else if (valLower.includes('not found') || valLower.includes('none')) {
                                badgeClass = 'badge-soft-secondary'; 
                              } else if (valLower.includes('softfail') || valLower.includes('warning')) {
                                badgeClass = 'badge-soft-warning'; 
                              }

                             if (valLower.includes('pass')) {
                               if (col.key === 'dkim') displayText = rawVal.toUpperCase();
                               else displayText = 'PASS';
                             } else if (valLower.includes('not found')) {
                               displayText = 'NOT FOUND';
                             } else if (valLower.includes('none')) {
                               displayText = 'NONE';
                             } else {
                               displayText = rawVal.split(' ')[0].toUpperCase();
                             }

                             return (
                               <span 
                                className={`badge ${badgeClass}`}
                                style={{ 
                                  whiteSpace: 'normal', 
                                  wordBreak: 'break-word', 
                                  textAlign: 'left',
                                  display: 'inline-block',
                                  width: '100%',
                                  padding: '5px 10px' 
                                }}
                               >
                                 {displayText}
                               </span>
                             );
                          })()
                        ) : col.key === 'domain' ? (
                          row.domain || row.auth?.domain || '—'
                        ) : col.key === 'sentDate' || col.key === 'receivedDate' ? (
                           row[col.key] ? formatDate(row[col.key]) : '—'
                        ) : (
                          String(row[col.key] || '—')
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
              
              {!isLoading && !error && results.length === 0 && (
                <tr>
                  <td colSpan={visibleColumns.length} className="text-center text-muted py-4">
                    <div className="text-warning">
                      <Info size={24} className="me-2" /> No results found.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}